<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="tutorials-installation" />

        <div class="markdown-content">
            <h1>GB WhatsApp Installation Guide – Complete Tutorial</h1>

            <p>This comprehensive guide will walk you through the complete process of installing GB WhatsApp on your Android device. Follow these steps carefully to ensure a smooth installation.</p>

            <h2>Introduction to Installation</h2>

            <p>Installing <a href="/" class="jump-url">GB WhatsApp</a> requires a few additional steps compared to regular apps since it's not available on the official Google Play Store. This guide covers everything you need to know for a successful installation.</p>

            <h2>Download Instructions</h2>

            <p>First, you need to download the GB WhatsApp APK file:</p>
            <ul>
                <li><strong>Official Source:</strong> Always download from trusted sources like our website</li>
                <li><strong>Version Check:</strong> Verify you're downloading the latest version</li>
                <li><strong>File Size:</strong> The APK file is approximately 50-60MB</li>
                <li><strong>File Extension:</strong> Ensure the file has .apk extension</li>
                <li><strong>Signature Verification:</strong> Check the APK signature for authenticity</li>
            </ul>

            <h2>Installation Steps</h2>

            <p>Follow these detailed installation steps:</p>
            <ol>
                <li><strong>Enable Unknown Sources:</strong> Go to Settings → Security → Enable "Unknown Sources"</li>
                <li><strong>Locate File:</strong> Find the downloaded APK file in your Downloads folder</li>
                <li><strong>Tap to Install:</strong> Tap the APK file to begin installation</li>
                <li><strong>Review Permissions:</strong> Carefully review all requested permissions</li>
                <li><strong>Confirm Installation:</strong> Tap "Install" to proceed with installation</li>
                <li><strong>Wait for Completion:</strong> Wait for the installation to complete</li>
                <li><strong>Open App:</strong> Tap "Open" to launch GB WhatsApp</li>
            </ol>

            <h2>Permission Settings</h2>

            <p>Configure permissions for optimal functionality:</p>
            <ul>
                <li><strong>Storage Access:</strong> Required for sending and receiving media files</li>
                <li><strong>Contacts Access:</strong> Needed to access your contact list</li>
                <li><strong>Camera Access:</strong> For taking photos and videos</li>
                <li><strong>Microphone Access:</strong> Required for voice messages and calls</li>
                <li><strong>Notification Access:</strong> For receiving message notifications</li>
            </ul>

            <h2>First Login</h2>

            <p>Set up your account on first launch:</p>
            <ol>
                <li><strong>Accept Terms:</strong> Accept the terms and conditions</li>
                <li><strong>Enter Phone Number:</strong> Input your phone number with country code</li>
                <li><strong>Verify Number:</strong> Enter the verification code sent via SMS</li>
                <li><strong>Restore Backup:</strong> Choose to restore from backup if available</li>
                <li><strong>Set Profile:</strong> Set your profile photo and name</li>
                <li><strong>Configure Settings:</strong> Adjust privacy and notification settings</li>
            </ol>

            <h2>Enable Features</h2>

            <p>Activate key features after installation:</p>
            <ul>
                <li><strong>Privacy Settings:</strong> Configure online status and read receipts</li>
                <li><strong>Auto-Reply:</strong> Set up auto-reply messages</li>
                <li><strong>Notifications:</strong> Customize notification sounds and LED colors</li>
                <li><strong>Themes:</strong> Apply your preferred visual theme</li>
                <li><strong>Security:</strong> Enable app lock and two-step verification</li>
            </ul>

            <h2>Common Installation Issues</h2>

            <p>Troubleshoot common problems:</p>
            <ul>
                <li><strong>Installation Blocked:</strong> Ensure unknown sources is enabled</li>
                <li><strong>Insufficient Storage:</strong> Free up space on your device</li>
                <li><strong>Version Conflict:</strong> Uninstall previous versions first</li>
                <li><strong>Verification Failed:</strong> Check your internet connection</li>
                <li><strong>Permission Denied:</strong> Grant all required permissions</li>
            </ul>

            <h2>Installation Notes</h2>

            <p>Important things to remember:</p>
            <ul>
                <li><strong>Backup First:</strong> Always backup your data before installing</li>
                <li><strong>Official Sources:</strong> Only download from trusted websites</li>
                <li><strong>Version Updates:</strong> Regularly check for new versions</li>
                <li><strong>Permission Safety:</strong> Understand why permissions are needed</li>
                <li><strong>Security:</strong> Enable all security features after installation</li>
            </ul>

            <h2>Uninstallation Process</h2>

            <p>If you need to uninstall GB WhatsApp:</p>
            <ol>
                <li>Go to Settings → Apps → GB WhatsApp</li>
                <li>Tap "Uninstall" and confirm the action</li>
                <li>Wait for the uninstallation to complete</li>
                <li>Restart your device if prompted</li>
            </ol>

            <h2>Related Topics</h2>

            <p>Explore more related guides:</p>
            <ul>
                <li><a href="/features/overview/">GB WhatsApp Features Overview</a> - Learn about all features</li>
                <li><a href="/use-cases/business/">Business Use Cases</a> - Using GB WhatsApp for business</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'TutorialsInstallation',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}
</style>
